import React, { useState, useEffect } from 'react';
import MMAllegroSetupBanner from './MMAllegroSetupBanner';
import ApiloNotesButton from './ApiloNotesButton';
import ApiloNotesPanel from './ApiloNotesPanel';
import axios from 'axios';

const API = '/api';

type Props = { onBack: () => void; openOrderId?: string; openAccountId?: string; openNonce?: number };

const CARRIER_LABEL: Record<string, string> = {
  INPOST: 'InPost',
  DPD: 'DPD',
  POCZTA_POLSKA: 'Poczta Polska',
  ALLEGRO: 'Allegro Dostawa',
  DHL: 'DHL',
  UPS: 'UPS',
  FEDEX: 'FedEx',
  GLS: 'GLS',
  ORLEN_PACZKA: 'Orlen Paczka',
};

function trackingUrl(carrierId: string | null, waybill: string): string | null {
  const w = encodeURIComponent(waybill);
  switch ((carrierId || '').toUpperCase()) {
    case 'INPOST': return `https://inpost.pl/sledzenie-przesylek?number=${w}`;
    case 'DPD': return `https://tracktrace.dpd.com.pl/parcelDetails?p1=${w}`;
    case 'POCZTA_POLSKA': return `https://emonitoring.poczta-polska.pl/?numer=${w}`;
    case 'DHL': return `https://www.dhl.com/pl-pl/home/tracking.html?tracking-id=${w}`;
    case 'UPS': return `https://www.ups.com/track?tracknum=${w}`;
    case 'FEDEX': return `https://www.fedex.com/fedextrack/?trknbr=${w}`;
    case 'GLS': return `https://gls-group.com/PL/pl/sledzenie-paczek?match=${w}`;
    case 'ORLEN_PACZKA': return `https://nadaj.orlenpaczka.pl/monitoring?n=${w}`;
    case 'ALLEGRO': return `https://allegro.pl/moje-allegro/zakupy/kupione`;
    default: return null;
  }
}

const money = (m: any) => (m && m.amount != null ? `${m.amount} ${m.currency || 'PLN'}` : '—');

export default function Shipments({ onBack, openOrderId = "", openAccountId = "", openNonce = 0 }: Props) {
  const [waybill, setWaybill] = useState('');
  const [accounts, setAccounts] = useState<{id:string,name:string}[]>([]);
  const [allegroBannerClosed, setAllegroBannerClosed] = useState(false);
  const [justReturnedFromAllegro] = useState(() => {
    try { return localStorage.getItem('mm_return_module') === 'shipments'; } catch (e) { return false; }
  });
  useEffect(() => {
    const t = localStorage.getItem('token') || '';
    axios.get(`${API}/allegro/accounts`, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => setAccounts(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (justReturnedFromAllegro) { try { localStorage.removeItem('mm_return_module'); } catch (e) {} }
  }, [justReturnedFromAllegro]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [details, setDetails] = useState<any | null>(null);
  const [matchedWaybill, setMatchedWaybill] = useState<string>('');
  const [copied, setCopied] = useState<string | null>(null);
  const [frame, setFrame] = useState<{ mode: 'messages' | 'issues' | 'apilo'; login: string } | null>(null);
  const [tracking, setTracking] = useState<Record<string, any>>({});
  const [trackingOpen, setTrackingOpen] = useState<Record<string, boolean>>({});

  const STATUS_PL: Record<string, string> = {
    PENDING: 'Przygotowana', IN_TRANSIT: 'W drodze', OUT_FOR_DELIVERY: 'W doreczeniu',
    READY_FOR_PICKUP: 'Gotowa do odbioru', DELIVERED: 'Doreczona', RETURNED: 'Zwrocona',
    CANCELLED: 'Anulowana', AVAILABLE_FOR_PICKUP: 'Do odbioru',
  };
  const STATUS_COLOR: Record<string, string> = {
    PENDING: '#6b7280', IN_TRANSIT: '#1d4ed8', OUT_FOR_DELIVERY: '#7c3aed',
    READY_FOR_PICKUP: '#0f766e', AVAILABLE_FOR_PICKUP: '#0f766e',
    DELIVERED: '#047857', RETURNED: '#b45309', CANCELLED: '#b91c1c',
  };

  const copy = (value: string, key: string) => {
    const text = String(value || '');
    if (!text) return;
    const done = () => { setCopied(key); setTimeout(() => setCopied(c => (c === key ? null : c)), 1400); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => fallbackCopy(text, done));
    } else {
      fallbackCopy(text, done);
    }
  };

  const fallbackCopy = (text: string, done: () => void) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select(); document.execCommand('copy');
      document.body.removeChild(ta); done();
    } catch { /* ignore */ }
  };

  // Telefon do formatu +48102147896 (bez spacji i myslnikow)
  const phoneRaw = (p?: string | null) => {
    if (!p) return '';
    let s = String(p).replace(/[\s\-()]/g, '');
    if (!s.startsWith('+')) s = s.length === 9 ? '+48' + s : (s.startsWith('48') ? '+' + s : s);
    return s;
  };

  const fetchTracking = async (accountId: string, shipments: any[]) => {
    await Promise.all((shipments || []).map(async (s: any) => {
      if (!s.waybill || !s.carrier_id) return;
      try {
        const r = await axios.get(`${API}/shipments/tracking`, {
          params: { carrier_id: s.carrier_id, waybill: s.waybill, account_id: accountId },
        });
        setTracking(t => ({ ...t, [s.waybill]: r.data }));
      } catch {
        setTracking(t => ({ ...t, [s.waybill]: { statuses: [], error: true } }));
      }
    }));
  };

  const toggleHistory = (waybill: string) =>
    setTrackingOpen(o => ({ ...o, [waybill]: !o[waybill] }));

  const search = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const q = waybill.trim();
    if (!q) return;
    setLoading(true); setError(null); setNotFound(false); setDetails(null); setFrame(null); setTracking({}); setTrackingOpen({});
    try {
      const res = await axios.get(`${API}/shipments/search`, { params: { waybill: q } });
      const results = res.data?.results || [];
      if (!results.length) { setNotFound(true); setLoading(false); return; }
      const first = results[0];
      setMatchedWaybill(q);
      const det = await axios.get(`${API}/shipments/order-details`, {
        params: { order_id: first.order_id, account_id: first.account_id },
      });
      const shipments = first.shipments || det.data.shipments || [];
      setDetails({ ...det.data, _shipments: shipments });
      // Historia przesylek pobierana przez nasze API - bez logowania do Allegro/przewoznika
      fetchTracking(first.account_id, shipments);
      setTrackingOpen(Object.fromEntries(shipments.map((s: any) => [s.waybill, true])));
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Blad wyszukiwania. Sprobuj ponownie.');
    } finally {
      setLoading(false);
    }
  };
  // Otwarcie konkretnego zamowienia po order_id (z wyszukiwarki Apilo) — bez numeru przesylki.
  const openByOrderId = async (orderId: string, accountId: string) => {
    setLoading(true); setError(null); setNotFound(false); setDetails(null); setFrame(null); setTracking({}); setTrackingOpen({});
    setWaybill('');
    try {
      const det = await axios.get(`${API}/shipments/order-details`, {
        params: { order_id: orderId, account_id: accountId },
      });
      const shipments = det.data.shipments || [];
      setMatchedWaybill('');
      setDetails({ ...det.data, _shipments: shipments });
      if (accountId) fetchTracking(accountId, shipments);
      setTrackingOpen(Object.fromEntries(shipments.map((s: any) => [s.waybill, true])));
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Nie udalo sie otworzyc zamowienia.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (openNonce === 0) return;
    const oid = (openOrderId || '').trim();
    if (oid) openByOrderId(oid, openAccountId || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openNonce]);

  const card: React.CSSProperties = {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
    padding: 16, marginBottom: 14,
  };
  const label: React.CSSProperties = { fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 };
  const h3: React.CSSProperties = { fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 10 };
  const copyable: React.CSSProperties = { cursor: 'pointer', borderBottom: '1px dashed #cbd5e1' };
  const Copied = ({ k }: { k: string }) => copied === k
    ? <span style={{ marginLeft: 6, fontSize: 11, color: '#047857', fontWeight: 700 }}>skopiowano</span>
    : null;

  const d = details;
  const addr = d?.delivery?.address;
  const pickup = d?.delivery?.pickup_point;

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui, sans-serif', color: '#111827' }}>
      {(accounts.length === 0 || justReturnedFromAllegro) && !allegroBannerClosed && (
        <MMAllegroSetupBanner
          message="Aby zarządzać przesyłkami, podłącz konto Allegro."
          returnModule="shipments"
          showCongrats={justReturnedFromAllegro}
          onClose={() => setAllegroBannerClosed(true)}
        />
      )}
      {/* Naglowek */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <button onClick={onBack} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>← Powrot</button>
        <span style={{ fontSize: 22 }}>🚚</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 17 }}>Przesylki</div>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>Wyszukaj zamowienie po numerze przesylki</div>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
        {/* Wyszukiwarka */}
        <form onSubmit={search} style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <input
            value={waybill}
            onChange={e => setWaybill(e.target.value)}
            placeholder="Wpisz numer przesylki (list przewozowy)"
            style={{ flex: 1, padding: '12px 14px', fontSize: 15, border: '1px solid #d1d5db', borderRadius: 8, outline: 'none' }}
            autoFocus
          />
          <button type="submit" disabled={loading || !waybill.trim()}
            style={{ padding: '12px 22px', fontSize: 15, fontWeight: 600, color: '#fff', background: loading ? '#9ca3af' : '#0f766e', border: 'none', borderRadius: 8, cursor: loading ? 'default' : 'pointer' }}>
            {loading ? 'Szukam…' : 'Szukaj'}
          </button>
        </form>

        {error && <div style={{ ...card, borderColor: '#fecaca', background: '#fef2f2', color: '#b91c1c' }}>{error}</div>}
        {notFound && (
          <div style={{ ...card, borderColor: '#fde68a', background: '#fffbeb', color: '#92400e' }}>
            Nie znaleziono zamowienia dla numeru <b>{waybill.trim()}</b>.
            <div style={{ fontSize: 12, marginTop: 6, color: '#b45309' }}>
              Przesylka moze byc spoza zsynchronizowanego okresu lub numer jest niepoprawny.
            </div>
          </div>
        )}

        {d && (
          <>
            {/* Podsumowanie zamowienia */}
            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <div style={label}>Zamowienie</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>
                    <span style={copyable} title="Kliknij, aby skopiowac" onClick={() => copy(d.order_id, 'order')}>{d.order_id}</span>
                    <Copied k="order" />
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                    Konto: <b>{d.account_name}</b> · Status: {d.status || '—'}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={label}>Do zaplaty</div>
                  <div style={{ fontWeight: 800, fontSize: 20, color: '#0f766e' }}>{money(d.summary?.total_to_pay)}</div>
                </div>
              </div>
            </div>

            {/* Przesylki */}
            <div style={card}>
              <div style={h3}>Przesylki ({(d._shipments || []).length})</div>
              {(d._shipments || []).map((s: any, i: number) => {
                const url = trackingUrl(s.carrier_id, s.waybill);
                const isMatch = s.waybill === matchedWaybill;
                return (
                  <div key={i}>
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                    padding: '10px 12px', borderRadius: 8, marginBottom: 6,
                    background: isMatch ? '#ecfdf5' : '#f9fafb',
                    border: `1px solid ${isMatch ? '#a7f3d0' : '#e5e7eb'}`,
                  }}>
                    <div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        {CARRIER_LABEL[s.carrier_id] || s.carrier_name || s.carrier_id || 'Przewoznik'}
                        {isMatch && <span style={{ marginLeft: 8, fontSize: 11, color: '#047857', fontWeight: 700 }}>← szukany numer</span>}
                      </div>
                      <span style={{ ...copyable, fontWeight: 700, fontSize: 15 }}
                        title="Kliknij, aby skopiowac numer"
                        onClick={() => copy(s.waybill, 'wb' + i)}>{s.waybill}</span>
                      <Copied k={'wb' + i} />
                      {url && (
                        <a href={url} target="_blank" rel="noreferrer"
                          style={{ marginLeft: 8, fontSize: 11, color: '#9ca3af', textDecoration: 'underline' }}>
                          strona przewoznika ↗
                        </a>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {tracking[s.waybill]?.last && (
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 12,
                          color: '#fff', background: STATUS_COLOR[tracking[s.waybill].last.code] || '#6b7280',
                        }}>
                          {STATUS_PL[tracking[s.waybill].last.code] || tracking[s.waybill].last.code}
                        </span>
                      )}
                      <span style={{ fontSize: 12, color: '#9ca3af' }}>
                        {s.created_at ? String(s.created_at).slice(0, 10) : ''}
                      </span>
                      <button onClick={() => toggleHistory(s.waybill)}
                        style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                          background: '#fff', border: '1px solid #d1d5db', color: '#374151' }}>
                        {trackingOpen[s.waybill] ? 'Ukryj historie' : 'Historia'}
                      </button>
                    </div>
                  </div>
                  {trackingOpen[s.waybill] && (
                    <div style={{ margin: '0 0 8px 12px', padding: '8px 12px', borderLeft: '2px solid #e5e7eb' }}>
                      {!tracking[s.waybill] && <div style={{ fontSize: 12, color: '#9ca3af' }}>Pobieram…</div>}
                      {tracking[s.waybill]?.error && <div style={{ fontSize: 12, color: '#b91c1c' }}>Nie udalo sie pobrac statusu.</div>}
                      {tracking[s.waybill] && !tracking[s.waybill].error && tracking[s.waybill].statuses?.length === 0 && (
                        <div style={{ fontSize: 12, color: '#9ca3af' }}>Brak historii statusow.</div>
                      )}
                      {(tracking[s.waybill]?.statuses || []).slice().reverse().map((st: any, j: number) => (
                        <div key={j} style={{ display: 'flex', gap: 10, fontSize: 12, padding: '3px 0' }}>
                          <span style={{ color: '#9ca3af', whiteSpace: 'nowrap', minWidth: 110 }}>
                            {String(st.occurred_at || '').slice(0, 16).replace('T', ' ')}
                          </span>
                          <span style={{ color: STATUS_COLOR[st.code] || '#374151', fontWeight: j === 0 ? 700 : 400 }}>
                            {st.description || st.code}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  </div>
                );
              })}
            </div>

            {/* Pozycje */}
            <div style={card}>
              <div style={h3}>Produkty ({(d.items || []).length})</div>
              {(d.items || []).map((it: any, i: number) => (
                <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0', borderTop: i ? '1px solid #f3f4f6' : 'none' }}>
                  {it.image_url
                    ? <img src={it.image_url} alt="" style={{ width: 54, height: 54, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb' }} />
                    : <div style={{ width: 54, height: 54, borderRadius: 6, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>📦</div>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{it.name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {it.sku ? <>SKU: <span style={copyable} title="Kliknij, aby skopiowac" onClick={() => copy(it.sku, 'sku' + i)}>{it.sku}</span><Copied k={'sku' + i} /> · </> : null}Ilosc: {it.quantity} · VAT {it.tax_rate}%
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap' }}>{money(it.price)}</div>
                </div>
              ))}
            </div>

            {/* Dostawa + kupujacy */}
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ ...card, flex: '1 1 320px' }}>
                <div style={h3}>Dostawa</div>
                <div style={{ fontSize: 13, lineHeight: 1.7 }}>
                  <div><b>{d.delivery?.method || '—'}</b></div>
                  <div style={{ color: '#6b7280' }}>Koszt: {money(d.delivery?.cost)}</div>
                  {pickup && (
                    <div style={{ marginTop: 8, padding: 8, background: '#eff6ff', borderRadius: 6, border: '1px solid #bfdbfe' }}>
                      <div style={{ fontSize: 11, color: '#1d4ed8', fontWeight: 700 }}>PUNKT ODBIORU</div>
                      <div>{pickup.name || pickup.id}</div>
                      {pickup.address && <div style={{ color: '#6b7280' }}>{pickup.address.street}, {pickup.address.zipCode} {pickup.address.city}</div>}
                    </div>
                  )}
                  {addr && (
                    <div style={{ marginTop: 8 }}>
                      <div>{addr.firstName} {addr.lastName}{addr.companyName ? ` (${addr.companyName})` : ''}</div>
                      <div style={{ color: '#6b7280' }}>{addr.street}</div>
                      <div style={{ color: '#6b7280' }}>{addr.zipCode} {addr.city}</div>
                      {addr.phoneNumber && <div style={{ color: '#6b7280' }}>tel. {addr.phoneNumber}</div>}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ ...card, flex: '1 1 320px' }}>
                <div style={h3}>Kupujacy</div>
                <div style={{ fontSize: 13, lineHeight: 1.7 }}>
                  <div><b>{d.buyer?.first_name} {d.buyer?.last_name}</b>{d.buyer?.company_name ? ` · ${d.buyer.company_name}` : ''}</div>
                  <div style={{ color: '#6b7280' }}>
                    <span style={copyable} title="Kliknij, aby skopiowac" onClick={() => copy(d.buyer?.login, 'login')}>{d.buyer?.login}</span>
                    <Copied k="login" />
                  </div>
                  {d.buyer?.email && <div style={{ color: '#6b7280' }}>{d.buyer.email}</div>}
                  {d.buyer?.phone && (
                    <div style={{ color: '#6b7280' }}>
                      tel. <span style={copyable} title={'Kliknij, aby skopiowac ' + phoneRaw(d.buyer.phone)} onClick={() => copy(phoneRaw(d.buyer.phone), 'phone')}>{d.buyer.phone}</span>
                      <Copied k="phone" />
                    </div>
                  )}
                </div>

                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f3f4f6' }}>
                  <div style={label}>{d.invoice?.has_invoice ? 'Faktura' : 'Paragon'}</div>
                  {d.invoice?.has_invoice ? (
                    <div style={{ fontSize: 13 }}>
                      {(d.invoice.issued || []).map((inv: any, i: number) => (
                        <div key={i} style={{ marginBottom: 6 }}>
                          <span style={{ ...copyable, fontWeight: 700, color: '#047857' }}
                            title="Kliknij, aby skopiowac numer faktury"
                            onClick={() => copy(inv.number, 'inv' + i)}>{inv.number}</span>
                          <Copied k={'inv' + i} />
                          {inv.created_at && (
                            <span style={{ color: '#9ca3af', marginLeft: 8, fontSize: 12 }}>
                              {String(inv.created_at).slice(0, 10)}
                            </span>
                          )}
                        </div>
                      ))}
                      {d.invoice.address && (
                        <div style={{ color: '#6b7280', lineHeight: 1.6, marginTop: 6 }}>
                          <div>{d.invoice.address.company?.name || `${d.invoice.address.naturalPerson?.firstName || ''} ${d.invoice.address.naturalPerson?.lastName || ''}`}</div>
                          {d.invoice.address.company?.taxId && (
                            <div>
                              NIP: <span style={copyable} title="Kliknij, aby skopiowac" onClick={() => copy(d.invoice.address.company.taxId, 'nip')}>{d.invoice.address.company.taxId}</span>
                              <Copied k="nip" />
                            </div>
                          )}
                          <div>{d.invoice.address.street}</div>
                          <div>{d.invoice.address.zipCode} {d.invoice.address.city}</div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                      Paragon
                      {d.invoice?.required && (
                        <span style={{ color: '#b45309', marginLeft: 8, fontSize: 12 }}>
                          (kupujacy zazadal faktury — jeszcze nie wystawiona)
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Rozmowy z kupujacym */}
            <div style={card}>
              <div style={h3}>Rozmowy z kupujacym</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  onClick={() => d.buyer?.login && setFrame({ mode: 'messages', login: d.buyer.login })}
                  disabled={!d.buyer?.login}
                  style={{
                    padding: '10px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13,
                    background: frame?.mode === 'messages' ? '#1d4ed8' : '#eff6ff',
                    color: frame?.mode === 'messages' ? '#fff' : '#1d4ed8',
                    border: '1px solid #bfdbfe',
                  }}>
                  Wiadomosci ({(d.links?.messages || []).length})
                </button>
                <button
                  onClick={() => d.buyer?.login && setFrame({ mode: 'issues', login: d.buyer.login })}
                  disabled={!d.buyer?.login}
                  style={{
                    padding: '10px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13,
                    background: frame?.mode === 'issues' ? '#b45309' : '#fffbeb',
                    color: frame?.mode === 'issues' ? '#fff' : '#b45309',
                    border: '1px solid #fde68a',
                  }}>
                  Dyskusje/Reklamacje ({(d.links?.issues || []).length})
                </button>
                {d.order_id && (
                  <button
                    onClick={() => setFrame(frame?.mode === 'apilo' ? null : { mode: 'apilo', login: d.order_id })}
                    style={{
                      padding: '10px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13,
                      background: frame?.mode === 'apilo' ? '#6d28d9' : '#f5f3ff',
                      color: frame?.mode === 'apilo' ? '#fff' : '#6d28d9',
                      border: '1px solid #ddd6fe',
                    }}>
                    {'\ud83d\udcdd Apilo - CRM'}
                  </button>
                )}
                {frame && (
                  <button onClick={() => setFrame(null)}
                    style={{ padding: '10px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                      background: '#f3f4f6', border: '1px solid #e5e7eb', color: '#6b7280' }}>
                    Zwin ✕
                  </button>
                )}
                <a href={d.buyer?.login ? `/?open=messages&login=${encodeURIComponent(d.buyer.login)}` : '#'}
                  target="_blank" rel="noreferrer"
                  style={{ fontSize: 12, color: '#6b7280', textDecoration: 'underline', marginLeft: 'auto' }}>
                  otworz w nowej karcie ↗
                </a>
              </div>

              {frame && frame.mode === 'apilo' && (
                <div style={{ marginTop: 12, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                  <div style={{ padding: '6px 10px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 11, color: '#6b7280' }}>
                    {'\ud83d\udcdd Wiadomosci z Apilo'}
                  </div>
                  <ApiloNotesPanel orderId={frame.login} />
                </div>
              )}
              {frame && frame.mode !== 'apilo' && (
                <div style={{ marginTop: 12, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                  <div style={{ padding: '6px 10px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 11, color: '#6b7280' }}>
                    {frame.mode === 'messages' ? 'Wiadomosci' : 'Dyskusje/Reklamacje'} — {frame.login} (filtr: wszystkie)
                  </div>
                  <iframe
                    key={frame.mode + frame.login}
                    title="rozmowy"
                    src={`/?open=${frame.mode}&login=${encodeURIComponent(frame.login)}`}
                    style={{ width: '100%', height: 620, border: 'none', display: 'block' }}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
